import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useStream } from '@laravel/stream-react';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChecklistIcon from '@mui/icons-material/Checklist';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StopIcon from '@mui/icons-material/Stop';
import moment from 'moment';
import Confirm from 'global/components/confirm';
import Markdown from 'global/components/Markdown';
import { get } from 'global/lib/lodash';
import sendData from 'redux/actions/sendData';
import { USER_INFO_SUCCESS } from 'redux/actions/types';
import {
  AGENT_CONVERSATIONS_PATH,
  deleteAgentConversation,
  deleteAgentConversations,
  getAgentConversation,
  getAgentUrl,
  listAgentConversations,
  renameAgentConversation,
  stopAgentConversation,
} from './agentApi';
import {
  AGENT_CONVERSATION_SORT_OPTIONS,
  getConversationId,
  getConversationTitle,
  getVisibleAgentConversations,
  getVisibleSelectionState,
  pruneConversationSelection,
  selectVisibleConversations,
  toggleConversationSelection,
} from './agentConversations';
import { AGENT_MODELS, getAgentModelFromConversation, getAgentModelFromUser, normalizeAgentModel } from './agentModels';
import AgentResultCards from './AgentResultCards';
import { createAgentStreamState, readAgentStreamChunk } from './stream';
import {
  AgentAttachButton,
  AgentAttachmentTray,
  AgentBulkToolbar,
  AgentConversationActions,
  AgentComposer,
  AgentConversationItem,
  AgentConversationList,
  AgentEmptyState,
  AgentMain,
  AgentMainHeader,
  AgentMessageBubble,
  AgentMessageRow,
  AgentMessages,
  AgentRoot,
  AgentSendButton,
  AgentSidebar,
  AgentSidebarControls,
  AgentSidebarHeader,
  AgentTextField,
} from './Agent.styles';

const emptyConversation = {
  id: null,
  title: 'New conversation',
};

const AGENT_RETURN_STATE_KEY = 'perform.agent.returnState';

const localId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const fileToAttachment = (file) => ({
  name: file.name,
  size: file.size,
  type: file.type,
});

const formatConversationTime = (conversation) => {
  const date = get(conversation, 'updated_at', get(conversation, 'created_at'));
  return date ? moment(date).fromNow() : '';
};

const ensureArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const getMessageText = (message) => {
  const content = get(message, 'content', '');
  if (typeof content === 'string') return content;
  if (content && typeof content === 'object') {
    return content.text || content.markdown || content.message || '';
  }
  return '';
};

const getMessageResults = (message) => [
  ...ensureArray(get(message, 'results')),
  ...ensureArray(get(message, 'components')),
  ...ensureArray(get(message, 'content.results')),
  ...ensureArray(get(message, 'content.components')),
];

const readAgentReturnState = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;

  try {
    const value = window.sessionStorage.getItem(AGENT_RETURN_STATE_KEY);
    if (!value) return null;
    const state = JSON.parse(value);
    return state && state.activeConversationId ? state : null;
  } catch (err) {
    return null;
  }
};

const writeAgentReturnState = (activeConversationId, scrollTop = 0) => {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  if (!activeConversationId) return;

  try {
    window.sessionStorage.setItem(
      AGENT_RETURN_STATE_KEY,
      JSON.stringify({
        activeConversationId: `${activeConversationId}`,
        scrollTop: Number.isFinite(scrollTop) ? scrollTop : 0,
      })
    );
  } catch (err) {
    // Session restore is opportunistic; private browsing/storage limits should not block the agent.
  }
};

const clearAgentReturnState = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return;

  try {
    window.sessionStorage.removeItem(AGENT_RETURN_STATE_KEY);
  } catch (err) {
    // Session restore is opportunistic; private browsing/storage limits should not block the agent.
  }
};

export default function AgentPage() {
  const token = useSelector((state) => get(state, 'auth.accessToken'));
  const currentUser = useSelector((state) => get(state, 'auth.user'));
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const fileInputRef = useRef(null);
  const messagesRef = useRef(null);
  const messagesEndRef = useRef(null);
  const streamStateRef = useRef(createAgentStreamState());
  const pendingAssistantIdRef = useRef(null);
  const activeConversationIdRef = useRef(null);
  const requestFailedRef = useRef(false);
  const pendingPromptRef = useRef(null);
  const stopRequestedRef = useRef(false);
  const savedReturnStateRef = useRef(readAgentReturnState());
  const returnStateRestoredRef = useRef(false);
  const pendingRestoreScrollTopRef = useRef(null);
  const restoringScrollRef = useRef(false);
  const agentModelRef = useRef(getAgentModelFromUser(currentUser));

  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState(null);
  const [retryPrompt, setRetryPrompt] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [conversationSearch, setConversationSearch] = useState('');
  const [conversationSort, setConversationSort] = useState(AGENT_CONVERSATION_SORT_OPTIONS.RECENT);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedConversationIds, setSelectedConversationIds] = useState([]);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [actionConversation, setActionConversation] = useState(null);
  const [renameConversation, setRenameConversation] = useState(null);
  const [renameTitle, setRenameTitle] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [conversationActionLoading, setConversationActionLoading] = useState(false);
  const [stoppingAgent, setStoppingAgent] = useState(false);
  const [agentModel, setAgentModel] = useState(getAgentModelFromUser(currentUser));
  const [agentModelSaving, setAgentModelSaving] = useState(false);
  const [agentModelSaveError, setAgentModelSaveError] = useState(null);
  const [confirm, setConfirm] = useState();

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  useEffect(() => {
    agentModelRef.current = agentModel;
  }, [agentModel]);

  const saveCurrentReturnState = useCallback(
    (scrollTop = messagesRef.current?.scrollTop || 0) => {
      if (!activeConversationIdRef.current) return;
      writeAgentReturnState(activeConversationIdRef.current, scrollTop);
    },
    []
  );

  const appendAssistantDelta = useCallback((delta) => {
    const assistantId = pendingAssistantIdRef.current;
    if (!assistantId || !delta) return;

    setMessages((current) =>
      current.map((message) => (message.id === assistantId ? { ...message, content: `${message.content || ''}${delta}` } : message))
    );
  }, []);

  const appendAssistantResults = useCallback((results) => {
    const assistantId = pendingAssistantIdRef.current;
    const nextResults = ensureArray(results);
    if (!assistantId || !nextResults.length) return;

    setMessages((current) =>
      current.map((message) => (message.id === assistantId ? { ...message, results: [...ensureArray(message.results), ...nextResults] } : message))
    );
  }, []);

  const loadConversations = useCallback(async () => {
    if (!token) return;

    setLoadingConversations(true);
    try {
      const response = await listAgentConversations(token);
      const nextConversations = get(response, 'data', []);
      setConversations(nextConversations);
      setSelectedConversationIds((current) => pruneConversationSelection(current, nextConversations));
    } catch (err) {
      setError(err.message);
    } finally {
      setConversationsLoaded(true);
      setLoadingConversations(false);
    }
  }, [token]);

  const loadConversation = useCallback(
    async (conversationId, opts = {}) => {
      if (!token || !conversationId) return;

      setLoadingMessages(true);
      setError(null);
      try {
        const response = await getAgentConversation(token, conversationId);
        if (opts.restoreScrollTop !== undefined) {
          pendingRestoreScrollTopRef.current = opts.restoreScrollTop;
          restoringScrollRef.current = true;
        }
        const conversation = get(response, 'data', {});
        setActiveConversationId(conversationId);
        setConversations((current) =>
          current.map((item) => (getConversationId(item) === `${conversationId}` ? { ...item, ...conversation, messages: undefined } : item))
        );
        setMessages(get(conversation, 'messages', []));
        setSidebarOpen(false);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingMessages(false);
      }
    },
    [token]
  );

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (pendingRestoreScrollTopRef.current !== null) {
      const scrollTop = pendingRestoreScrollTopRef.current;
      pendingRestoreScrollTopRef.current = null;
      window.requestAnimationFrame(() => {
        if (messagesRef.current) {
          messagesRef.current.scrollTop = scrollTop;
          saveCurrentReturnState(scrollTop);
        }
        restoringScrollRef.current = false;
      });
      return;
    }

    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, saveCurrentReturnState]);

  useEffect(() => {
    if (returnStateRestoredRef.current || !conversationsLoaded || loadingConversations || loadingMessages) return;

    const savedState = savedReturnStateRef.current;
    if (!savedState?.activeConversationId) return;

    returnStateRestoredRef.current = true;
    const conversationExists = conversations.some((conversation) => getConversationId(conversation) === `${savedState.activeConversationId}`);
    if (!conversationExists) {
      clearAgentReturnState();
      return;
    }

    loadConversation(savedState.activeConversationId, { restoreScrollTop: savedState.scrollTop || 0 });
  }, [conversations, conversationsLoaded, loadConversation, loadingConversations, loadingMessages]);

  useEffect(() => {
    if (!activeConversationId) return;
    saveCurrentReturnState();
  }, [activeConversationId, saveCurrentReturnState]);

  useEffect(() => {
    if (selectedConversationIds.length === 0) {
      setSelectionMode(false);
    }
  }, [selectedConversationIds]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || selectedConversationIds.length === 0) return;
      setSelectedConversationIds([]);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedConversationIds]);

  const activeConversation = useMemo(
    () => conversations.find((conversation) => getConversationId(conversation) === `${activeConversationId}`) || emptyConversation,
    [activeConversationId, conversations]
  );

  const userDefaultAgentModel = useMemo(() => getAgentModelFromUser(currentUser), [currentUser]);
  const activeAgentModel = activeConversationId
    ? getConversationId(activeConversation)
      ? getAgentModelFromConversation(activeConversation)
      : normalizeAgentModel(agentModelRef.current)
    : userDefaultAgentModel;

  useEffect(() => {
    setAgentModel(activeAgentModel);
  }, [activeAgentModel]);

  const visibleConversations = useMemo(
    () => getVisibleAgentConversations(conversations, conversationSearch, conversationSort),
    [conversationSearch, conversationSort, conversations]
  );

  const selectedConversationCount = selectedConversationIds.length;
  const selectedVisibleState = useMemo(
    () => getVisibleSelectionState(selectedConversationIds, visibleConversations),
    [selectedConversationIds, visibleConversations]
  );

  const handleStreamResponse = useCallback((response) => {
    const conversationId = response.headers.get('X-Agent-Conversation-Id');
    if (conversationId) {
      activeConversationIdRef.current = conversationId;
      setActiveConversationId(conversationId);
    }
  }, []);

  const handleStreamData = useCallback(
    (chunk) => {
      const event = readAgentStreamChunk(chunk, streamStateRef.current);
      if (event.error) {
        requestFailedRef.current = true;
        setError(event.error);
      }
      appendAssistantDelta(event.delta);
      appendAssistantResults(event.results);
    },
    [appendAssistantDelta, appendAssistantResults]
  );

  const completeStoppedRequest = useCallback(() => {
    const assistantId = pendingAssistantIdRef.current;

    if (assistantId) {
      setMessages((current) =>
        current.map((message) =>
          message.id === assistantId && !getMessageText(message) && !getMessageResults(message).length ? { ...message, content: 'The agent response was stopped.' } : message
        )
      );
    }

    pendingAssistantIdRef.current = null;
    pendingPromptRef.current = null;
    streamStateRef.current = createAgentStreamState();
    requestFailedRef.current = false;
    setRetryPrompt(null);
    setStoppingAgent(false);
    if (activeConversationIdRef.current) {
      loadConversations();
    }
  }, [loadConversations]);

  const handleStreamError = useCallback((err) => {
    if (stopRequestedRef.current || err.name === 'AbortError') return;

    requestFailedRef.current = true;
    setError(err.message);
    setRetryPrompt(pendingPromptRef.current);
  }, []);

  const handleStreamFinish = useCallback(() => {
    if (stopRequestedRef.current) {
      stopRequestedRef.current = false;
      completeStoppedRequest();
      return;
    }

    const assistantId = pendingAssistantIdRef.current;
    pendingAssistantIdRef.current = null;
    streamStateRef.current = createAgentStreamState();

    if (requestFailedRef.current) {
      if (assistantId) {
        setMessages((current) =>
          current.map((message) => (message.id === assistantId && !message.content ? { ...message, content: 'The agent request failed.' } : message))
        );
      }
      requestFailedRef.current = false;
      return;
    }

    pendingPromptRef.current = null;
    loadConversations();
  }, [completeStoppedRequest, loadConversations]);

  const handleStreamCancel = useCallback(() => {
    if (!stopRequestedRef.current) return;
    completeStoppedRequest();
  }, [completeStoppedRequest]);

  const streamOptions = useMemo(
    () => ({
      headers: {
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'omit',
      onResponse: handleStreamResponse,
      onData: handleStreamData,
      onError: handleStreamError,
      onCancel: handleStreamCancel,
      onFinish: handleStreamFinish,
    }),
    [handleStreamCancel, handleStreamData, handleStreamError, handleStreamFinish, handleStreamResponse, token]
  );

  const agentStream = useStream(getAgentUrl(AGENT_CONVERSATIONS_PATH), streamOptions);
  const isSending = agentStream.isFetching || agentStream.isStreaming;
  const conversationControlsDisabled = isSending || conversationActionLoading;
  const agentModelSelectDisabled = isSending || agentModelSaving || !!activeConversationId || !token;

  const saveAgentModelPreference = useCallback(
    async (nextAgentModel) => {
      const userId = get(currentUser, 'id');
      if (!userId) return;

      const meta = {
        ...get(currentUser, 'meta', {}),
        agent_model: nextAgentModel,
      };

      setAgentModelSaving(true);
      setAgentModelSaveError(null);
      try {
        await sendData(
          {
            url: `/users/${userId}`,
            method: 'patch',
            data: {
              data: {
                type: 'users',
                id: `${userId}`,
                attributes: {
                  meta,
                },
              },
            },
          },
          { simulate: false }
        );
        dispatch({ type: USER_INFO_SUCCESS, payload: { ...currentUser, meta } });
      } catch (err) {
        setAgentModelSaveError('Agent preference could not be saved.');
      } finally {
        setAgentModelSaving(false);
      }
    },
    [currentUser, dispatch]
  );

  const handleAgentModelChange = (event) => {
    const nextAgentModel = normalizeAgentModel(event.target.value);
    setAgentModel(nextAgentModel);
    saveAgentModelPreference(nextAgentModel);
  };

  const startNewConversation = useCallback(() => {
    clearAgentReturnState();
    setActiveConversationId(null);
    setAgentModel(userDefaultAgentModel);
    setMessages([]);
    setPrompt('');
    setAttachments([]);
    setError(null);
    setRetryPrompt(null);
    setSelectedConversationIds([]);
    setSidebarOpen(false);
    pendingRestoreScrollTopRef.current = null;
    restoringScrollRef.current = false;
  }, [userDefaultAgentModel]);

  const closeConversationMenu = () => {
    setActionAnchorEl(null);
    setActionConversation(null);
  };

  const removeDeletedConversations = useCallback(
    (conversationIds = []) => {
      const deletedIds = conversationIds.map((conversationId) => `${conversationId}`);

      setConversations((current) => current.filter((conversation) => !deletedIds.includes(getConversationId(conversation))));
      setSelectedConversationIds((current) => current.filter((conversationId) => !deletedIds.includes(`${conversationId}`)));

      if (deletedIds.includes(`${activeConversationIdRef.current}`)) {
        startNewConversation();
      }
    },
    [startNewConversation]
  );

  const openConversationMenu = (event, conversation) => {
    event.stopPropagation();
    setActionAnchorEl(event.currentTarget);
    setActionConversation(conversation);
  };

  const openRenameDialog = (conversation) => {
    closeConversationMenu();
    setRenameConversation(conversation);
    setRenameTitle(getConversationTitle(conversation));
  };

  const closeRenameDialog = () => {
    if (renameSaving) return;
    setRenameConversation(null);
    setRenameTitle('');
  };

  const submitRename = async (event) => {
    event.preventDefault();
    const title = renameTitle.trim();
    const conversationId = getConversationId(renameConversation);
    if (!title || !conversationId || !token) return;

    setRenameSaving(true);
    setError(null);
    try {
      const response = await renameAgentConversation(token, conversationId, title);
      const updatedConversation = get(response, 'data', { ...renameConversation, title });
      setConversations((current) =>
        current.map((conversation) =>
          getConversationId(conversation) === conversationId
            ? {
                ...conversation,
                ...updatedConversation,
                title: get(updatedConversation, 'title', title),
              }
            : conversation
        )
      );
      setRenameConversation(null);
      setRenameTitle('');
    } catch (err) {
      setError(err.message);
    } finally {
      setRenameSaving(false);
    }
  };

  const confirmDeleteConversation = (conversation) => {
    closeConversationMenu();
    setConfirm({
      title: 'Delete conversation?',
      content: `"${getConversationTitle(conversation)}" will be removed from your conversation list.`,
      okayText: 'Delete',
      onCancel: () => setConfirm(),
      onOk: async () => {
        const conversationId = getConversationId(conversation);
        setConversationActionLoading(true);
        setError(null);
        try {
          await deleteAgentConversation(token, conversationId);
          removeDeletedConversations([conversationId]);
          setConfirm();
        } catch (err) {
          setError(err.message);
        } finally {
          setConversationActionLoading(false);
        }
      },
    });
  };

  const confirmBulkDelete = () => {
    setConfirm({
      title: `Delete ${selectedConversationCount} conversations?`,
      content: 'Selected conversations will be removed from your conversation list.',
      okayText: 'Delete',
      onCancel: () => setConfirm(),
      onOk: async () => {
        setConversationActionLoading(true);
        setError(null);
        try {
          const result = await deleteAgentConversations(token, selectedConversationIds);
          removeDeletedConversations(result.deletedIds);
          setConfirm();

          if (result.hasFailures) {
            setError(`${result.failed.length} conversations could not be deleted.`);
          }
        } finally {
          setConversationActionLoading(false);
        }
      },
    });
  };

  const toggleSelectionMode = () => {
    if (conversationControlsDisabled) return;
    setSelectionMode((current) => {
      if (current) {
        setSelectedConversationIds([]);
      }
      return !current;
    });
  };

  const toggleConversation = (conversationId) => {
    if (conversationControlsDisabled) return;
    setSelectedConversationIds((current) => toggleConversationSelection(current, conversationId));
  };

  const handleConversationClick = (conversation) => {
    if (selectionMode) {
      toggleConversation(getConversationId(conversation));
      return;
    }

    loadConversation(conversation.id);
  };

  const sendPrompt = (messageText = prompt) => {
    const cleanPrompt = messageText.trim();
    if (!cleanPrompt || isSending || !token) return;

    const userMessage = {
      id: localId('user'),
      role: 'user',
      content: cleanPrompt,
      attachments: attachments.map(fileToAttachment),
    };
    const assistantMessage = {
      id: localId('assistant'),
      role: 'assistant',
      content: '',
    };

    stopRequestedRef.current = false;
    requestFailedRef.current = false;
    pendingPromptRef.current = cleanPrompt;
    streamStateRef.current = createAgentStreamState();
    pendingAssistantIdRef.current = assistantMessage.id;
    setMessages((current) => [...current, userMessage, assistantMessage]);
    setPrompt('');
    setAttachments([]);
    setError(null);
    setRetryPrompt(null);
    agentStream.clearData();
    agentStream.send({
      prompt: cleanPrompt,
      conversation_id: activeConversationIdRef.current,
      attachments: userMessage.attachments,
      agent_model: normalizeAgentModel(agentModelRef.current),
    });
  };

  const stopResponse = async () => {
    if (!isSending || !token || stoppingAgent) return;

    stopRequestedRef.current = true;
    requestFailedRef.current = false;
    setStoppingAgent(true);
    setRetryPrompt(null);
    try {
      await stopAgentConversation(token, {
        streamId: agentStream.id,
        conversationId: activeConversationIdRef.current,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      agentStream.cancel();
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendPrompt();
  };

  const handleFilesSelected = (event) => {
    const files = Array.from(event.target.files || []);
    setAttachments((current) => [...current, ...files]);
    event.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments((current) => current.filter((_, i) => i !== index));
  };

  const handleMessagesScroll = (event) => {
    if (restoringScrollRef.current) return;
    saveCurrentReturnState(event.currentTarget.scrollTop);
  };

  const renderSidebar = () => (
    <AgentSidebar>
      <AgentSidebarHeader>
        <SmartToyIcon color="primary" />
        <Typography variant="subtitle1" sx={{ flex: 1 }}>
          Agent
        </Typography>
        <Tooltip title={selectionMode ? 'Clear selection' : 'Select conversations'}>
          <span>
            <IconButton aria-label={selectionMode ? 'Clear selection' : 'Select conversations'} onClick={toggleSelectionMode} disabled={conversationControlsDisabled} size="small">
              {selectionMode ? <CloseIcon fontSize="small" /> : <ChecklistIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <IconButton aria-label="Refresh conversations" onClick={loadConversations} disabled={loadingConversations || isSending} size="small">
          {loadingConversations ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
        </IconButton>
        <IconButton aria-label="New conversation" onClick={startNewConversation} disabled={isSending} size="small">
          <AddIcon fontSize="small" />
        </IconButton>
        {isMobile && (
          <IconButton aria-label="Close conversations" onClick={() => setSidebarOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </AgentSidebarHeader>
      <AgentSidebarControls>
        <TextField
          value={conversationSearch}
          onChange={(event) => setConversationSearch(event.target.value)}
          placeholder="Search conversations"
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: conversationSearch ? (
                <InputAdornment position="end">
                  <IconButton aria-label="Clear conversation search" edge="end" size="small" onClick={() => setConversationSearch('')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
            htmlInput: { 'aria-label': 'Search conversations' },
          }}
        />
        <FormControl size="small">
          <InputLabel id="agent-conversation-sort-label">Sort</InputLabel>
          <Select
            labelId="agent-conversation-sort-label"
            label="Sort"
            value={conversationSort}
            onChange={(event) => setConversationSort(event.target.value)}
            inputProps={{ 'aria-label': 'Sort conversations' }}
          >
            <MenuItem value={AGENT_CONVERSATION_SORT_OPTIONS.RECENT}>Recent activity</MenuItem>
            <MenuItem value={AGENT_CONVERSATION_SORT_OPTIONS.TITLE}>Title</MenuItem>
          </Select>
        </FormControl>
      </AgentSidebarControls>
      {selectionMode && (
        <AgentBulkToolbar>
          <Checkbox
            size="small"
            checked={selectedVisibleState.allVisibleSelected}
            indeterminate={selectedVisibleState.someVisibleSelected}
            disabled={!visibleConversations.length || conversationControlsDisabled}
            inputProps={{ 'aria-label': 'Select all visible conversations' }}
            onChange={(event) => {
              if (event.target.checked) {
                setSelectedConversationIds((current) => selectVisibleConversations(current, visibleConversations));
              } else {
                const visibleIds = visibleConversations.map(getConversationId);
                setSelectedConversationIds((current) => current.filter((conversationId) => !visibleIds.includes(`${conversationId}`)));
              }
            }}
          />
          <Typography variant="body2" sx={{ flex: 1 }}>
            {selectedConversationCount ? `${selectedConversationCount} selected` : 'Select conversations'}
          </Typography>
          <IconButton aria-label="Delete selected conversations" onClick={confirmBulkDelete} disabled={!selectedConversationCount || conversationControlsDisabled} color="error" size="small">
            {conversationActionLoading ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
          </IconButton>
        </AgentBulkToolbar>
      )}
      <AgentConversationList>
        <AgentConversationItem selected={!activeConversationId} onClick={startNewConversation} disabled={isSending || selectionMode}>
          <ListItemText primary="New conversation" secondary="Start a fresh agent thread" />
        </AgentConversationItem>
        {loadingConversations && conversations.length === 0 ? (
          <AgentEmptyState>
            <CircularProgress size={22} />
          </AgentEmptyState>
        ) : visibleConversations.length === 0 ? (
          <AgentEmptyState>{conversationSearch ? 'No conversations match your search.' : 'No saved conversations yet.'}</AgentEmptyState>
        ) : (
          visibleConversations.map((conversation) => {
            const conversationId = getConversationId(conversation);
            const checked = selectedConversationIds.includes(conversationId);

            return (
              <AgentConversationItem
                key={conversationId}
                selected={getConversationId(conversation) === `${activeConversationId}`}
                onClick={() => handleConversationClick(conversation)}
                disabled={conversationControlsDisabled}
                data-testid={`agent-conversation-${conversation.id}`}
              >
                {selectionMode && (
                  <Checkbox
                    size="small"
                    checked={checked}
                    inputProps={{ 'aria-label': `Select ${getConversationTitle(conversation)}` }}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => toggleConversation(conversationId)}
                  />
                )}
                <ListItemText primary={getConversationTitle(conversation)} secondary={formatConversationTime(conversation)} />
                {!selectionMode && (
                  <AgentConversationActions>
                    <IconButton
                      aria-label={`Conversation actions for ${getConversationTitle(conversation)}`}
                      onClick={(event) => openConversationMenu(event, conversation)}
                      disabled={conversationControlsDisabled}
                      size="small"
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </AgentConversationActions>
                )}
              </AgentConversationItem>
            );
          })
        )}
      </AgentConversationList>
      <Menu anchorEl={actionAnchorEl} open={Boolean(actionAnchorEl)} onClose={closeConversationMenu}>
        <MenuItem onClick={() => openRenameDialog(actionConversation)} disabled={!actionConversation || conversationControlsDisabled}>
          <EditIcon fontSize="small" sx={{ marginRight: 1 }} />
          Rename
        </MenuItem>
        <MenuItem onClick={() => confirmDeleteConversation(actionConversation)} disabled={!actionConversation || conversationControlsDisabled}>
          <DeleteIcon fontSize="small" sx={{ marginRight: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </AgentSidebar>
  );

  return (
    <AgentRoot>
      {!isMobile && renderSidebar()}
      <Drawer open={sidebarOpen} onClose={() => setSidebarOpen(false)}>
        {renderSidebar()}
      </Drawer>
      <AgentMain>
        <AgentMainHeader>
          {isMobile && (
            <IconButton aria-label="Open conversations" onClick={() => setSidebarOpen(true)} edge="start">
              <MenuIcon />
            </IconButton>
          )}
          <SmartToyIcon color="primary" />
          <div style={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" noWrap>
              {activeConversation.title}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {isSending ? 'Agent is responding' : activeConversationId ? 'Continuing thread' : 'Ready for a new thread'}
            </Typography>
          </div>
          <FormControl size="small" sx={{ minWidth: 132, flexShrink: 0 }} disabled={agentModelSelectDisabled}>
            <InputLabel id="agent-model-label">Agent</InputLabel>
            <Select
              labelId="agent-model-label"
              label="Agent"
              value={agentModel}
              onChange={handleAgentModelChange}
              inputProps={{ 'aria-label': 'Agent model' }}
            >
              {AGENT_MODELS.map((model) => (
                <MenuItem key={model.value} value={model.value}>
                  {model.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </AgentMainHeader>

        <AgentMessages ref={messagesRef} onScroll={handleMessagesScroll} data-testid="agent-message-list">
          {agentModelSaveError && (
            <Alert severity="warning" onClose={() => setAgentModelSaveError(null)}>
              {agentModelSaveError}
            </Alert>
          )}
          {error && (
            <Alert
              severity="error"
              action={
                retryPrompt ? (
                  <IconButton aria-label="Retry prompt" color="inherit" size="small" onClick={() => sendPrompt(retryPrompt)}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                ) : null
              }
            >
              {error}
            </Alert>
          )}
          {loadingMessages ? (
            <CircularProgress sx={{ alignSelf: 'center', marginTop: 4 }} />
          ) : messages.length === 0 ? (
            <Typography color="textSecondary" align="center" sx={{ marginTop: 6 }}>
              Ask the Perform agent about accessible locations, assessments, and tasks.
            </Typography>
          ) : (
            messages.map((message) => (
              <AgentMessageRow key={message.id} role={message.role}>
                <AgentMessageBubble role={message.role}>
                  {getMessageText(message) ? (
                    <Markdown>{getMessageText(message)}</Markdown>
                  ) : getMessageResults(message).length ? null : (
                    <CircularProgress size={18} />
                  )}
                  <AgentResultCards results={getMessageResults(message)} />
                  {(message.attachments || []).length > 0 && (
                    <AgentAttachmentTray sx={{ marginTop: 1 }}>
                      {message.attachments.map((attachment, index) => (
                        <Chip key={`${attachment.name}-${index}`} size="small" icon={<AttachFileIcon />} label={attachment.name} />
                      ))}
                    </AgentAttachmentTray>
                  )}
                </AgentMessageBubble>
              </AgentMessageRow>
            ))
          )}
          <div ref={messagesEndRef} />
        </AgentMessages>

        <AgentComposer onSubmit={handleSubmit}>
          <input ref={fileInputRef} type="file" multiple hidden onChange={handleFilesSelected} />
          <AgentAttachButton aria-label="Attach files" onClick={() => fileInputRef.current?.click()} disabled={isSending}>
            <AttachFileIcon />
          </AgentAttachButton>
          <AgentTextField
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="Ask the Perform agent"
            multiline
            minRows={1}
            maxRows={5}
            disabled={isSending || !token}
            slotProps={{ htmlInput: { 'aria-label': 'Agent prompt' } }}
          />
          <AgentSendButton
            type={isSending ? 'button' : 'submit'}
            variant="contained"
            color={isSending ? 'error' : 'secondary'}
            disabled={isSending ? stoppingAgent : !prompt.trim() || !token}
            aria-label={isSending ? 'Stop response' : 'Send prompt'}
            onClick={isSending ? stopResponse : undefined}
          >
            {isSending && stoppingAgent ? <CircularProgress size={20} color="inherit" /> : isSending ? <StopIcon /> : <SendIcon />}
          </AgentSendButton>
          {attachments.length > 0 && (
            <AgentAttachmentTray>
              {attachments.map((file, index) => (
                <Chip key={`${file.name}-${index}`} size="small" icon={<AttachFileIcon />} label={file.name} onDelete={() => removeAttachment(index)} />
              ))}
            </AgentAttachmentTray>
          )}
        </AgentComposer>
      </AgentMain>
      <Dialog open={!!renameConversation} onClose={closeRenameDialog} fullWidth maxWidth="xs">
        <form onSubmit={submitRename}>
          <DialogTitle>Rename conversation</DialogTitle>
          <DialogContent dividers>
            <TextField
              autoFocus
              fullWidth
              label="Conversation title"
              value={renameTitle}
              onChange={(event) => setRenameTitle(event.target.value)}
              disabled={renameSaving}
              inputProps={{ maxLength: 120 }}
              margin="dense"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeRenameDialog} disabled={renameSaving} variant="text">
              Cancel
            </Button>
            <Button type="submit" disabled={!renameTitle.trim() || renameSaving} color="secondary" variant="contained">
              {renameSaving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Confirm {...confirm} />
    </AgentRoot>
  );
}
